"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.drawTextOnPath = drawTextOnPath;

var _math = require("../../math.js");

var _transform = require("./transform.js");

/**
 * @module ol/geom/flat/textpath
 */

/**
 * @param {Array<number>} flatCoordinates Path to put text on.
 * @param {number} offset Start offset of the `flatCoordinates`.
 * @param {number} end End offset of the `flatCoordinates`.
 * @param {number} stride Stride.
 * @param {string} text Text to place on the path.
 * @param {number} startM m along the path where the text starts.
 * @param {number} maxAngle Max angle between adjacent chars in radians.
 * @param {number} scale The product of the text scale and the device pixel ratio.
 * @param {function(string, string, Object<string, number>):number} measureAndCacheTextWidth Measure and cache text width.
 * @param {string} font The font.
 * @param {Object<string, number>} cache A cache of measured widths.
 * @param {number} rotation Rotation to apply to the flatCoordinates to determine whether text needs to be reversed.
 * @return {Array<Array<*>>|null} The result array (or null if `maxAngle` was
 * exceeded). Entries of the array are x, y, anchorX, angle, chunk.
 */
function drawTextOnPath(flatCoordinates, offset, end, stride, text, startM, maxAngle, scale, measureAndCacheTextWidth, font, cache, rotation) {
  var x2 = flatCoordinates[offset];
  var y2 = flatCoordinates[offset + 1];
  var x1 = 0;
  var y1 = 0;
  var segmentLength = 0;
  var segmentM = 0;

  function advance() {
    x1 = x2;
    y1 = y2;
    offset += stride;
    x2 = flatCoordinates[offset];
    y2 = flatCoordinates[offset + 1];
    segmentM += segmentLength;
    segmentLength = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
  }

  do {
    advance();
  } while (offset < end - stride && segmentM + segmentLength < startM);

  var interpolate = segmentLength === 0 ? 0 : (startM - segmentM) / segmentLength;
  var beginX = (0, _math.lerp)(x1, x2, interpolate);
  var beginY = (0, _math.lerp)(y1, y2, interpolate);
  var startOffset = offset - stride;
  var startLength = segmentM;
  var endM = startM + scale * measureAndCacheTextWidth(font, text, cache);

  while (offset < end - stride && segmentM + segmentLength < endM) {
    advance();
  }

  interpolate = segmentLength === 0 ? 0 : (endM - segmentM) / segmentLength;
  var endX = (0, _math.lerp)(x1, x2, interpolate);
  var endY = (0, _math.lerp)(y1, y2, interpolate); // Keep text upright

  var reverse;

  if (rotation) {
    var flat = [beginX, beginY, endX, endY];
    (0, _transform.rotate)(flat, 0, 4, 2, rotation, flat, flat);
    reverse = flat[0] > flat[2];
  } else {
    reverse = beginX > endX;
  }

  var PI = Math.PI;
  var result = [];
  var singleSegment = startOffset + stride === offset;
  offset = startOffset;
  segmentLength = 0;
  segmentM = startLength;
  x2 = flatCoordinates[offset];
  y2 = flatCoordinates[offset + 1];
  var previousAngle; // All on the same segment

  if (singleSegment) {
    advance();
    previousAngle = Math.atan2(y2 - y1, x2 - x1);

    if (reverse) {
      previousAngle += previousAngle > 0 ? -PI : PI;
    }

    var x = (endX + beginX) / 2;
    var y = (endY + beginY) / 2;
    result[0] = [x, y, (endM - startM) / 2, previousAngle, text];
    return result;
  }

  for (var i = 0, ii = text.length; i < ii;) {
    advance();
    var angle = Math.atan2(y2 - y1, x2 - x1);

    if (reverse) {
      angle += angle > 0 ? -PI : PI;
    }

    if (previousAngle !== undefined) {
      var delta = angle - previousAngle;
      delta += delta > PI ? -2 * PI : delta < -PI ? 2 * PI : 0;

      if (Math.abs(delta) > maxAngle) {
        return null;
      }
    }

    previousAngle = angle;
    var iStart = i;
    var charLength = 0;

    for (; i < ii; ++i) {
      var index = reverse ? ii - i - 1 : i;
      var len = scale * measureAndCacheTextWidth(font, text[index], cache);

      if (offset + stride < end && segmentM + segmentLength < startM + charLength + len / 2) {
        break;
      }

      charLength += len;
    }

    if (i === iStart) {
      continue;
    }

    var chars = reverse ? text.substring(ii - iStart, ii - i) : text.substring(iStart, i);
    interpolate = segmentLength === 0 ? 0 : (startM + charLength / 2 - segmentM) / segmentLength;
    var x = (0, _math.lerp)(x1, x2, interpolate);
    var y = (0, _math.lerp)(y1, y2, interpolate);
    result.push([x, y, charLength / 2, angle, chars]);
    startM += charLength;
  }

  return result;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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